// Owl Hour Stake Drawer — stake selector + projected payout.
//
// Opens when user clicks "Lock [tier]". Shows:
//   - Current tier name + combined odds
//   - Adjustable stake input with preset buttons
//   - Live projected payout calculation
//   - Lock / dismiss actions
//
// Phantom-only: no real money flows. The "Lock" action is
// informational — records the user's intended stake in meta.

import { useCallback, useEffect, useState } from "react";
import type { OwlHourSlip, OwlTier } from "../../owl/useOwlHourSlips.ts";
import { OWL_TIER_META } from "../../owl/useOwlHourSlips.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

// ─── Preset stakes per tier ──────────────────────────────────

const TIER_PRESETS: Record<OwlTier, number[]> = {
  rollover:     [20, 50, 100, 200],
  moderate:     [10, 25, 50, 100],
  conservative: [5, 10, 25, 50],
  aggressive:   [2, 5, 10, 25],
  owl_1000:     [1, 2, 5, 10],
};

// ─── Component ──────────────────────────────────────────────

export interface OwlStakeDrawerProps {
  tier: OwlTier;
  slip: OwlHourSlip | undefined;
  open: boolean;
  onClose: () => void;
  /** Called when user locks a stake — parent uses this to update signal card */
  onLock?: (stake: number) => void;
}

export function OwlStakeDrawer({
  tier,
  slip,
  open,
  onClose,
  onLock,
}: OwlStakeDrawerProps): JSX.Element | null {
  const meta = OWL_TIER_META[tier];
  const combinedOdds = slip?.combined_odds ?? 0;
  const legCount = slip?.legs.length ?? 0;
  const presets = TIER_PRESETS[tier];

  // String-based input so user can clear the field
  const [stakeStr, setStakeStr] = useState(String(meta.defaultStake));
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const stakeNum = Number(stakeStr) || 0;
  const payout = Math.round(stakeNum * combinedOdds);

  // Reset when tier changes or drawer reopens
  useEffect(() => {
    if (open) {
      setStakeStr(String(meta.defaultStake));
      setLocked(false);
    }
  }, [open, meta.defaultStake]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handlePreset = useCallback((amount: number) => {
    setStakeStr(String(amount));
    setLocked(false);
  }, []);

  const handleCustom = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow empty (user is clearing) or valid positive numbers
    if (raw === "" || raw === "0") {
      setStakeStr(raw);
      setLocked(false);
      return;
    }
    const val = Number(raw);
    if (!isNaN(val) && val >= 0 && val <= 10000) {
      setStakeStr(raw);
      setLocked(false);
    }
  }, []);

  const handleLock = useCallback(async () => {
    if (stakeNum <= 0 || !slip || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { error } = await (getBrowserClient().rpc as any)("lock_owl_publication", { p_id: slip.id, p_stake: stakeNum });
      if (error) throw error;
      setLocked(true);
      onLock?.(stakeNum);
    } catch {
      setSaveError("Couldn't save this ticket. Refresh and try again.");
    } finally { setSaving(false); }
  }, [stakeNum, onLock, slip, saving]);

  if (!open) return null;

  return (
    <div className="owl-stake-overlay" onClick={onClose}>
      <div
        className="owl-stake-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="owl-stake-head">
          <div>
            <div className="tier-label">{meta.label}</div>
            <div className="tier-name">{meta.name}</div>
          </div>
          <button type="button" className="close" onClick={onClose}>✕</button>
        </div>

        {/* Slip summary */}
        {slip ? (
          <div className="owl-stake-summary">
            <div className="row">
              <span className="k">Legs</span>
              <span className="v">{legCount}</span>
            </div>
            <div className="row">
              <span className="k">Combined odds</span>
              <span className="v odds">{combinedOdds.toFixed(2)}×</span>
            </div>
            <div className="row">
              <span className="k">Leagues</span>
              <span className="v">{slip.leagues_covered.join(" / ")}</span>
            </div>
          </div>
        ) : (
          <div className="owl-stake-summary">
            <div className="empty">
              Kickoffs run from 19:00 to 07:00 Accra. Set your stake —
              it locks automatically when the slip drops.
            </div>
          </div>
        )}

        {/* Stake input */}
        <div className="owl-stake-input">
          <label className="label">Your stake</label>
          <div className="input-row">
            <span className="currency">GH₵</span>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*"
              placeholder="0"
              value={stakeStr}
              onChange={handleCustom}
              className="amount"
            />
          </div>
          <div className="presets">
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                className={`preset ${stakeNum === p ? "active" : ""}`}
                onClick={() => handlePreset(p)}
              >
                GH₵{p}
              </button>
            ))}
          </div>
        </div>

        {/* Payout projection */}
        <div className="owl-stake-payout">
          <div className="label">If all {legCount > 0 ? legCount : 0} legs hit</div>
          <div className="amount">
            {combinedOdds > 0 && stakeNum > 0 ? (
              <>
                GH₵{stakeNum} → <span className="target">GH₵{payout.toLocaleString()}</span>
              </>
            ) : combinedOdds > 0 && stakeNum === 0 ? (
              <span className="pending">Enter a stake above</span>
            ) : (
              <span className="pending">Awaiting tonight's slip…</span>
            )}
          </div>
          {combinedOdds > 0 && stakeNum > 0 && (
            <div className="return">
              {((combinedOdds - 1) * 100).toFixed(0)}% return · {combinedOdds.toFixed(1)}× multiplier
            </div>
          )}
        </div>

        {/* Action */}
        {saveError ? <div role="alert">{saveError}</div> : null}
        <div className="owl-stake-actions" aria-busy={saving}>
          {locked ? (
            <div className="locked-msg">
              ✓ Locked at GH₵{stakeNum} — payout GH₵{payout.toLocaleString()} if all hit.
              <button type="button" className="btn" onClick={() => setLocked(false)}>
                Change
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn primary full"
              onClick={handleLock}
              disabled={stakeNum <= 0 || saving || !slip}
            >
              {stakeNum > 0 && combinedOdds > 0
                ? `Lock ${meta.name} · GH₵${stakeNum} → GH₵${payout.toLocaleString()}`
                : `Lock ${meta.name}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
